import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { format } from 'date-fns';
import { DomainStatsWithComparison } from '../../../../shared/types/monitor/data/sources/DomainStatsWithComparison';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import CustomLink from '../../../../shared/components/custom-link/CustomLink';
import './DomainsStatsTable.scss';
import { TrendTag } from '../../../projects/preview/components/trend-tag/TrendTag';
import { getTrendValue } from '../../../../shared/utils/utils';
import { Skeleton } from 'primereact/skeleton';
import EmptyPanel from '../../../../shared/components/empty-panel/EmptyPanel';
import DomainsFilter from '../domains-filter/DomainsFilter';
import { ChatType } from '../../../../shared/types/monitor/ChatType';

interface DomainsStatsTableProps {
  domainsStats: DomainStatsWithComparison[] | null;
  selectedDomain: string | null;
  onDomainSelect: (domain: string | null) => void;
  startDate: Date | null;
  endDate: Date | null;
  previousStartDate: Date | null;
  previousEndDate: Date | null;
  isLoading: boolean;
  selectedDomains: string[];
  projectId: number;
  chatType: ChatType;
  onDomainsChange: (domains: string[]) => void;
}

export const DomainsStatsTable: React.FC<DomainsStatsTableProps> = ({
  domainsStats,
  selectedDomain,
  onDomainSelect,
  startDate,
  endDate,
  previousStartDate,
  previousEndDate,
  isLoading,
  selectedDomains,
  projectId,
  chatType,
  onDomainsChange,
}) => {
  const { t } = useTranslation();
  const dataTableRef = useRef<any>(null);
  const [clickedDomain, setClickedDomain] = useState<string | null>(null);
  const clickedDomainRef = useRef<string | null>(null);
  const domainsStatsRef = useRef<DomainStatsWithComparison[]>();

  // Synchronize refs with state/props
  useEffect(() => {
    clickedDomainRef.current = clickedDomain;
  }, [clickedDomain]);

  useEffect(() => {
    if (domainsStats === null) {
      return;
    }

    domainsStatsRef.current = domainsStats;
  }, [domainsStats]);

  // Reset clickedDomain when domainsStats change significantly (e.g., new domains added)
  useEffect(() => {
    if (clickedDomain && !domainsStats?.find((d) => d.domain === clickedDomain)) {
      setClickedDomain(null);
    }
  }, [domainsStats, clickedDomain]);

  const handleRowClick = useCallback(
    (event: any) => {
      const domainToSelect = event.data.domain;
      const newClickedDomain = domainToSelect === clickedDomainRef.current ? null : domainToSelect;
      setClickedDomain(newClickedDomain);
      onDomainSelect(newClickedDomain);
    },
    [onDomainSelect]
  );

  const handleRowMouseEnter = useCallback(
    (domain: string) => {
      onDomainSelect(domain);
    },
    [onDomainSelect]
  );

  const handleRowMouseLeave = useCallback(() => {
    onDomainSelect(clickedDomainRef.current);
  }, [onDomainSelect]);

  useEffect(() => {
    const tableElement = dataTableRef.current?.el?.querySelector('.p-datatable-tbody');
    if (!tableElement) return;

    let cleanup: (() => void) | null = null;

    // Use setTimeout to ensure table is fully rendered
    const timeoutId = setTimeout(() => {
      const rows = tableElement.querySelectorAll('tr');

      const handleMouseEnter = (e: Event) => {
        const row = e.currentTarget as HTMLTableRowElement;
        const index = Array.from(rows).indexOf(row);

        if (index >= 0 && domainsStatsRef && domainsStatsRef.current && domainsStatsRef.current[index]) {
          handleRowMouseEnter(domainsStatsRef.current[index].domain);
        }
      };

      const handleMouseLeave = () => {
        handleRowMouseLeave();
      };

      rows.forEach((row: HTMLTableRowElement) => {
        row.addEventListener('mouseenter', handleMouseEnter);
        row.addEventListener('mouseleave', handleMouseLeave);
      });

      cleanup = () => {
        rows.forEach((row: HTMLTableRowElement) => {
          row.removeEventListener('mouseenter', handleMouseEnter);
          row.removeEventListener('mouseleave', handleMouseLeave);
        });
      };
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      if (cleanup) {
        cleanup();
      }
    };
  }, [domainsStats, handleRowMouseEnter, handleRowMouseLeave]);

  const formatDateRange = (start: Date | null, end: Date | null): string => {
    if (!start || !end) return '';
    return `${format(start, 'dd.MM')} - ${format(end, 'dd.MM.yyyy')}`;
  };

  const formatShare = (share: number): string => {
    // Cap at 100% maximum
    const cappedShare = Math.min(share, 100);
    return `${cappedShare.toFixed(2)}%`;
  };

  // Generate keys for columns with dates to force re-render when dates change
  const currentPeriodKey =
    startDate && endDate ? `${startDate.getTime()}-${endDate.getTime()}` : 'no-dates';
  const previousPeriodKey =
    previousStartDate && previousEndDate
      ? `${previousStartDate.getTime()}-${previousEndDate.getTime()}`
      : 'no-dates';

  return (
    <div className="stats__panel">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('performanceByDomain')}
          <CustomTooltip tooltipText="performanceByDomainTooltip" addMargin={false} />
        </div>
        <div className="domains-stats-table__filter">
          <DomainsFilter
            projectId={projectId}
            chatType={chatType}
            from={startDate!}
            to={endDate!}
            selectedDomains={selectedDomains}
            onChange={onDomainsChange}
          />
        </div>
      </div>

      {isLoading || domainsStats === null ? (
        <Skeleton className="skeleton--table skeleton--table-4">
          <span />
          <span />
          <span />
          <span />
        </Skeleton>
      ) : (
        <DataTable
          stripedRows
          scrollable
          removableSort
          value={domainsStats}
          tableClassName="table"
          className="table-wrapper"
          sortField={'currentCount'}
          sortOrder={-1}
          onRowClick={handleRowClick}
          ref={dataTableRef}
          selectionMode="single"
          emptyMessage={() => <EmptyPanel />}
          selection={domainsStats.find((d) => d.domain === selectedDomain) ?? null}
        >
          <Column
            header="#"
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            body={(_rowData, { rowIndex }) => rowIndex + 1}
            style={{ width: '8px', minWidth: '8px' }}
          />
          <Column
            field="domain"
            header={t('domain')}
            headerClassName="table__header"
            body={(rowData: DomainStatsWithComparison) => (
              <div className="source-data">
                <span
                  className="source-data__icon"
                  style={{ backgroundImage: `url("https://favicon.is/${rowData.domain}")` }}
                />
                <CustomLink url={rowData.domain} target="_blank" addHttps={true} />
              </div>
            )}
            style={{ minWidth: '120px', maxWidth: '200px' }}
          />
          <Column
            key={`previous-mentions-${previousPeriodKey}`}
            header={
              <div className="cell-header">
                <div>
                  <span id="domains-stats-prev-mentions-header">{t('performanceByDomainMentions')}</span>
                  <CustomTooltip
                    tooltipText="performanceByDomainMentionsTooltip"
                    targetRef="domains-stats-prev-mentions-header"
                  />
                </div>
                {previousStartDate && previousEndDate && (
                  <div className="cell-header__details">
                    {formatDateRange(previousStartDate, previousEndDate)}
                  </div>
                )}
              </div>
            }
            headerClassName="table__header table__header--center table__header--relative"
            bodyClassName="table__cell--center table__cell--grey"
            style={{ width: '75px', minWidth: '75px' }}
            body={(rowData: DomainStatsWithComparison) => rowData.previousCount}
          />
          <Column
            key={`current-mentions-${currentPeriodKey}`}
            header={
              <div className="cell-header">
                <div>
                  <span id="domains-stats-curr-mentions-header">{t('performanceByDomainMentions')}</span>
                  <CustomTooltip
                    tooltipText="performanceByDomainMentionsTooltip"
                    targetRef="domains-stats-curr-mentions-header"
                  />
                </div>
                {startDate && endDate && (
                  <div className="cell-header__details">{formatDateRange(startDate, endDate)}</div>
                )}
              </div>
            }
            headerClassName="table__header table__header--center table__header--relative"
            bodyClassName={`table__cell--center`}
            style={{ width: '75px', minWidth: '75px' }}
            body={(rowData: DomainStatsWithComparison) => rowData.currentCount}
          />
          <Column
            header={
              <>
                <span id="domains-stats-mentions-change-header">
                  {t('performanceByDomainMentionsChange')}
                </span>
                <CustomTooltip
                  tooltipText="performanceByDomainMentionsChangeTooltip"
                  targetRef="domains-stats-mentions-change-header"
                />
              </>
            }
            headerClassName="table__header table__header--center"
            bodyClassName={`table__cell--center table__cell--right-border`}
            style={{ width: '55px', minWidth: '55px' }}
            body={(rowData: DomainStatsWithComparison) => (
              <TrendTag trendValue={getTrendValue(rowData.countChange)} suffix="" />
            )}
          />
          <Column
            key={`previous-share-${previousPeriodKey}`}
            header={
              <div className="cell-header">
                <div>
                  <span id="domains-stats-prev-share-header">{t('performanceByDomainShare')}</span>
                  <CustomTooltip
                    tooltipText="performanceByDomainShareTooltip"
                    targetRef="domains-stats-prev-share-header"
                  />
                </div>
                {previousStartDate && previousEndDate && (
                  <div className="cell-header__details">
                    {formatDateRange(previousStartDate, previousEndDate)}
                  </div>
                )}
              </div>
            }
            headerClassName="table__header table__header--center table__header--relative"
            bodyClassName="table__cell--center table__cell--grey"
            style={{ width: '70px', minWidth: '70px' }}
            body={(rowData: DomainStatsWithComparison) => formatShare(rowData.previousShare)}
          />
          <Column
            key={`current-share-${currentPeriodKey}`}
            header={
              <div className="cell-header">
                <div>
                  <span id="domains-stats-curr-share-header">{t('performanceByDomainShare')}</span>
                  <CustomTooltip
                    tooltipText="performanceByDomainShareTooltip"
                    targetRef="domains-stats-curr-share-header"
                  />
                </div>
                {startDate && endDate && (
                  <div className="cell-header__details">{formatDateRange(startDate, endDate)}</div>
                )}
              </div>
            }
            headerClassName="table__header table__header--center table__header--relative"
            bodyClassName={`table__cell--center`}
            style={{ width: '70px', minWidth: '70px' }}
            body={(rowData: DomainStatsWithComparison) => formatShare(rowData.currentShare)}
          />
          <Column
            header={
              <>
                <span id="domains-stats-share-change-header">{t('performanceByDomainShareChange')}</span>
                <CustomTooltip
                  tooltipText="performanceByDomainShareChangeTooltip"
                  targetRef="domains-stats-share-change-header"
                />
              </>
            }
            headerClassName="table__header table__header--center"
            bodyClassName={`table__cell--center`}
            style={{ width: '70px', minWidth: '70px' }}
            body={(rowData: DomainStatsWithComparison) => (
              <TrendTag trendValue={getTrendValue(rowData.shareChange, 2)} suffix="%" />
            )}
          />
        </DataTable>
      )}
    </div>
  );
};
